import React, { useEffect, useState } from "react";
import { Button, IconButton, Typography } from "@mui/material";
import {
  DownloadRounded,
  DescriptionOutlined,
  ErrorOutline,
} from "@mui/icons-material";
import CheckIcon from "@mui/icons-material/Check";
import apiService from "../../../Services/APIService";
import FileIconPdf from "../../../../images/file-type-pdf.svg";
import { s360ConfigService } from "../../../Services/S360Config";
import { convertEpochToTimestamp } from "../../../../utils/dateConversion";
import "./NCXComponents.styles.css";
import flowMemorySingleton from "../../../Services/FlowMemorySingleton";

function NCXMediaView(props) {
  const {
    mediaData,
    id,
    agentText = false,
    downloadInProgress,
    creator,
    messageStatus,
    senderId,
    senderName,
    messageId,
    receivedTimestamp,
    workitemId,
  } = props;

  const [agentImage, setAgentImage] = useState(false);
  const [mediaUrls, setMediaUrls] = useState({});

  const senderNameStyle = {
    fontWeight: "bold",
    marginBottom: "0.25rem",
    fontSize: "x-small",
  };

  const calculateFileSize = (value) => {
    value = Math.floor(parseFloat(value / 1024));
    const KB_IN_MB = 1024;
    const KB_IN_GB = 1024 * 1024;

    if (value >= KB_IN_GB) return (value / KB_IN_GB).toFixed(2) + " gb";
    if (value >= KB_IN_MB) return (value / KB_IN_MB).toFixed(2) + " mb";
    return value + " kb";
  };

  const selectFileIcon = (attachmentMimeType) => {
    switch (attachmentMimeType) {
      case "application/pdf":
        return <img src={FileIconPdf} alt="PDF" />;
      default:
        return (
          <DescriptionOutlined fontSize="large" sx={{ color: "#7A7D8C" }} />
        );
    }
  };

  function convertGcsDownloadUrl(url) {
    try {
      const u = new URL(url);

      if (
        u.hostname === "storage.googleapis.com" &&
        u.pathname.startsWith("/download/storage/v1") &&
        u.searchParams.get("alt") === "media"
      ) {
        const bucketMatch = u.pathname.match(/\/b\/([^/]+)\/o\/(.+)/);
        if (!bucketMatch) return url;

        const bucket = bucketMatch[1];
        const objectPath = decodeURIComponent(bucketMatch[2]);

        return `https://storage.googleapis.com/${bucket}/${objectPath}`;
      }

      return url;
    } catch (e) {
      console.error("Invalid URL:", url, e);
      return url;
    }
  }

  const renderMessageStatus = () => {
    if (s360ConfigService.config?.sandBox) {
      return (
        <div className="s360-message-status">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 448 512"
            style={{
              marginLeft: "5px",
              height: "16px",
              width: "16px",
              color: "rgb(74, 237, 14)",
            }}
            fill="currentColor"
          >
            <path d="M337 81c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0l-143 143L97 127c-9.4-9.4-24.6-9.4-33.9 0s-9.4 24.6 0 33.9l80 80c9.4 9.4 24.6 9.4 33.9 0L337 81zM441 201c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0l-247 247L41 295c-9.4-9.4-24.6-9.4-33.9 0s-9.4 24.6 0 33.9L143 465c9.4 9.4 24.6 9.4 33.9 0L441 201z" />
          </svg>
        </div>
      );
    }

    // Default to "SENT" for USER/CUSTOMER messages when status is not available
    const isUserOrCustomer = creator === "USER" || creator === "CUSTOMER";

    // Determine effective status with proper fallback
    let effectiveStatus = null;
    if (isUserOrCustomer) {
      if (Array.isArray(messageStatus) && messageStatus.length > 0) {
        // If messageStatus is an array, get the latest status
        const latestStatus = messageStatus[messageStatus.length - 1];
        effectiveStatus = latestStatus?.status || latestStatus || "SENT";
      } else if (typeof messageStatus === "string" && messageStatus) {
        effectiveStatus = messageStatus;
      } else {
        // Default to SENT for user/customer messages with no status
        effectiveStatus = "SENT";
      }
    }

    if (effectiveStatus) {
      switch (effectiveStatus) {
        case "SENT":
          return (
            <div className="s360-message-status">
              <CheckIcon
                sx={{
                  marginLeft: "5px",
                  color: "#aaaaaa",
                  height: "16px",
                  width: "16px",
                }}
              />
            </div>
          );
        case "DELIVERED":
          return (
            <div className="s360-message-status">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 448 512"
                style={{
                  marginLeft: "5px",
                  height: "16px",
                  width: "16px",
                  color: "#aaaaaa",
                }}
                fill="currentColor"
              >
                <path d="M337 81c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0l-143 143L97 127c-9.4-9.4-24.6-9.4-33.9 0s-9.4 24.6 0 33.9l80 80c9.4 9.4 24.6 9.4 33.9 0L337 81zM441 201c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0l-247 247L41 295c-9.4-9.4-24.6-9.4-33.9 0s-9.4 24.6 0 33.9L143 465c9.4 9.4 24.6 9.4 33.9 0L441 201z" />
              </svg>
            </div>
          );

        case "READ":
          return (
            <div className="s360-message-status">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 448 512"
                style={{
                  marginLeft: "5px",
                  height: "16px",
                  width: "16px",
                  color: "rgb(74, 237, 14)",
                }}
                fill="currentColor"
              >
                <path d="M337 81c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0l-143 143L97 127c-9.4-9.4-24.6-9.4-33.9 0s-9.4 24.6 0 33.9l80 80c9.4 9.4 24.6 9.4 33.9 0L337 81zM441 201c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0l-247 247L41 295c-9.4-9.4-24.6-9.4-33.9 0s-9.4 24.6 0 33.9L143 465c9.4 9.4 24.6 9.4 33.9 0L441 201z" />
              </svg>
            </div>
          );
        default:
          return (
            <div className="s360-message-status">
              <ErrorOutline
                sx={{
                  marginLeft: "5px",
                  color: "#aaaaaa",
                  height: "16px",
                  width: "16px",
                }}
              />
              <span className="failed-message-status">Failed to send</span>
            </div>
          );
      }
    }
    return null;
  };
  const selectViewFromMediaType = (url, mData) => {
    const mimeType = (
      mData.mediaMimeType ||
      mData.fileType ||
      ""
    ).toLowerCase();
    const mediaType = (mData.mediaType || "").toUpperCase();
    const urlLower = url.toLowerCase();

    const isImage =
      ["image/png", "image/jpg", "image/jpeg", "image/gif"].some((m) =>
        mimeType.startsWith(m),
      ) ||
      mediaType === "IMAGE" ||
      [".png", ".jpg", ".jpeg", ".gif"].some((ext) => urlLower.includes(ext));
    const isVideo =
      mimeType.startsWith("video/") ||
      mediaType === "VIDEO" ||
      [".mp4", ".avi", ".webm"].some((ext) => urlLower.includes(ext));
    const isAudio =
      mimeType.startsWith("audio/") ||
      mediaType === "AUDIO" ||
      [".mp3", ".wav", ".ogg", ".flac", ".aac"].some((ext) =>
        urlLower.includes(ext),
      );

    if (isImage) {
      const handleImageClick = (e) => {
        e.preventDefault();
        const imageUrl = convertGcsDownloadUrl(url);
        const newWindow = window.open();
        if (newWindow) {
          newWindow.opener = null;
          newWindow.location = imageUrl;
        }
      };

      return (
        <>
          <img
            className={downloadInProgress ? "" : "mediaViewCommon"}
            style={
              downloadInProgress
                ? { width: "100px", objectFit: "contain", cursor: "pointer" }
                : { cursor: "pointer" }
            }
            src={url}
            alt={mData.title || mData.caption || "Media"}
            onClick={handleImageClick}
          />
          <div className="s360-message-receipt-container">
            {s360ConfigService.config.configDetails.showReadReceipt &&
              renderMessageStatus()}
          </div>
        </>
      );
    }

    if (isVideo) {
      return (
        <>
          <video className="mediaViewCommon" controls controlsList="nodownload">
            <source src={url} />
          </video>
          <div className="s360-message-receipt-container">
            {s360ConfigService.config.configDetails.showReadReceipt &&
              renderMessageStatus()}
          </div>
        </>
      );
    }
    if (isAudio) {
      return (
        <>
          <audio className="mediaViewCommon" controls controlsList="nodownload">
            <source src={url} />
            Your browser does not support the audio element.
          </audio>
          <div className="s360-message-receipt-container">
            {s360ConfigService.config.configDetails.showReadReceipt &&
              renderMessageStatus()}
          </div>
        </>
      );
    }
    return null;
  };

  const generateAgentAvatar = () => {
    if (s360ConfigService.config?.sandBox) {
      return (
        <svg
          width="30"
          height="30"
          viewBox="0 0 100 100"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          style={{ marginRight: "8px" }}
        >
          <circle
            cx="50"
            cy="50"
            r="48"
            stroke="#D1D5DB"
            strokeWidth="4"
            fill="#F9FAFB"
          />
          <circle cx="50" cy="35" r="15" fill="#9CA3AF" />
          <path
            d="M20 80c0-16.57 13.43-30 30-30s30 13.43 30 30"
            fill="#9CA3AF"
          />
        </svg>
      );
    }
    if (agentImage) {
      return (
        <img
          src={agentImage}
          alt=""
          style={{
            width: "30px",
            height: "30px",
            borderRadius: "20px",
            marginRight: "8px",
            objectFit: "cover",
          }}
        />
      );
    }
  };

  const fetchNewAgentImage = async (senderId) => {
    let imgData = await apiService.getAgentImage(senderId);
    setAgentImage(imgData?.data?.signedURL);
    flowMemorySingleton.setCurrentAgentImageUrl(imgData?.data?.signedURL);
  };

  useEffect(() => {
    if (creator === "AGENT") {
      if (senderId !== flowMemorySingleton.getCurrentAgentUserId()) {
        flowMemorySingleton.setCurrentAgentUserId(senderId);
        fetchNewAgentImage(senderId);
      } else {
        setAgentImage(flowMemorySingleton.getCurrentAgentImageUrl());
      }
    }
  }, []);

  const fetchAndStoreUrl = async (mData) => {
    try {
      const result = await apiService.getMediaUrl(workitemId, mData.title);
      const newUrl = result?.data?.signedURL;
      const key =
        mData.title || mData.mediaUrl || mData.id || Math.random().toString();
      if (newUrl && mediaUrls[key] !== newUrl) {
        setMediaUrls((prev) => ({ ...prev, [key]: newUrl }));
      }
    } catch (e) {
      console.warn("Media URL fetch failed:", e);
    }
  };

  const MediaViewItem = ({ mData }) => {
    const key =
      mData.title || mData.mediaUrl || mData.id || Math.random().toString();
    const urlInState = mediaUrls[key];
    let immediateUrl = mData.url || mData.mediaUrl;
    if (
      mData.mediaPath &&
      (mData.mediaPath.startsWith("http") || mData.mediaPath.startsWith("blob"))
    ) {
      immediateUrl = mData.mediaPath;
    }

    useEffect(() => {
      if (!urlInState) {
        if (agentText) {
          fetchAndStoreUrl(mData);
        } else if (immediateUrl) {
          setMediaUrls((prev) => ({ ...prev, [key]: immediateUrl }));
        }
      }
    }, [mData, immediateUrl]);

    const url = mediaUrls[key] || immediateUrl;
    if (!url) return null;

    const fileName = mData.title || mData.caption || "download";
    const fileMimeType = mData.mediaMimeType || mData.fileType || "";
    const fileSize = mData.mediaSize;

    const isPreview = (() => {
      const mimeType = (fileMimeType || "").toLowerCase();
      const mediaType = (mData.mediaType || "").toUpperCase();
      if (
        mimeType.startsWith("image/") ||
        mimeType.startsWith("video/") ||
        mimeType.startsWith("audio/")
      )
        return true;
      if (["IMAGE", "VIDEO", "AUDIO"].includes(mediaType)) return true;
      return [
        ".png",
        ".jpg",
        ".jpeg",
        ".gif",
        ".mp4",
        ".avi",
        ".webm",
        ".mp3",
        ".wav",
        ".ogg",
        ".flac",
        ".aac",
      ].some((type) => url.toLowerCase().includes(type));
    })();

    return (
      <div>
        {creator === "AGENT" && senderName && (
          <div
            style={{
              ...senderNameStyle,
              paddingLeft:
                !!agentImage &&
                s360ConfigService?.config?.configDetails?.showAgentInfo
                  ? "40px"
                  : "0",
            }}
            className="sender-name-agent"
          >
            {senderName()} - {convertEpochToTimestamp(receivedTimestamp)}
          </div>
        )}

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent:
              creator === "AGENT" || creator === "BOT"
                ? "flex-start"
                : "flex-end",
          }}
        >
          {creator === "AGENT" &&
            senderId &&
            s360ConfigService?.config?.configDetails?.showAgentInfo &&
            generateAgentAvatar()}
          <div className="mediaContainer">
            {isPreview ? (
              <div className="mediaWrapper">
                {selectViewFromMediaType(url, mData)}
              </div>
            ) : (
              <>
                <div className="mediaViewNonPreview">
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    {selectFileIcon(fileMimeType)}
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <span
                        style={{
                          fontWeight: "800",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          width: "39vw",
                        }}
                      >
                        {fileName}
                      </span>
                      {fileSize && <span>{calculateFileSize(fileSize)}</span>}
                    </div>
                  </div>
                  <IconButton
                    onClick={async () => {
                      try {
                        // Fetch the file as a blob to prevent navigation
                        const response = await fetch(url);
                        const blob = await response.blob();

                        // Create a temporary URL for the blob
                        const blobUrl = window.URL.createObjectURL(blob);

                        // Create a hidden anchor element
                        const link = document.createElement("a");
                        link.href = blobUrl;
                        link.download = fileName || "download";
                        link.style.display = "none";

                        // Append, click, and cleanup
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);

                        // Revoke the blob URL to free memory
                        setTimeout(
                          () => window.URL.revokeObjectURL(blobUrl),
                          100,
                        );
                      } catch (error) {
                        console.error("Download failed:", error);
                        // Fallback to direct download if fetch fails
                        const link = document.createElement("a");
                        link.href = url;
                        link.download = fileName || "download";
                        link.style.display = "none";
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                      }
                    }}
                  >
                    <DownloadRounded />
                  </IconButton>
                </div>
                <div className="s360-message-receipt-container">
                  {s360ConfigService.config.configDetails.showReadReceipt &&
                    renderMessageStatus()}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      className="mediaViewContainer"
      style={{
        alignItems:
          creator === "AGENT" || creator === "BOT" ? "flex-start" : "flex-end",
      }}
    >
      {mediaData.map((mData, index) => (
        <MediaViewItem
          key={mData.title || mData.mediaUrl || mData.id || index}
          mData={mData}
        />
      ))}
    </div>
  );
}

export default NCXMediaView;
